import Alpine from 'alpinejs';
import axios from '../../config/axios.js';

function createInvitationCatalogManager() {
  return {
    apiBase: '',
    tab: 'categories',
    categories: [],
    layouts: [],
    components: [],
    componentGroups: {},
    viewOptions: {},
    loading: false,
    saving: false,
    modal: false,
    modalTitle: '',
    catForm: { id: null, name: '', slug: '', icon: '', sort_order: 0, is_active: true },
    layoutForm: { id: null, key: '', label: '', description: '', view: 'invitations.layouts.classic', sort_order: 0, is_active: true, is_builtin: false },
    componentForm: { id: null, key: '', label: '', component_type: 'section', section_type: 'opening', view: 'invitations.components.sections.opening', sort_order: 0, is_active: true },

    init() {
      try {
        const cfg = JSON.parse(this.$el.getAttribute('data-config') || '{}');
        this.apiBase = cfg.apiBase || '/api/v1/kasir/engagement';
      } catch (e) {
        console.error(e);
      }
      const params = new URLSearchParams(window.location.search);
      const initialTab = params.get('tab');
      if (['categories', 'layouts', 'components'].includes(initialTab)) {
        this.tab = initialTab;
      }
      this.fetchAll();
    },

    async fetchAll() {
      this.loading = true;
      try {
        const [catRes, layoutRes, componentRes] = await Promise.all([
          axios.get(`${this.apiBase}/invitation-categories`),
          axios.get(`${this.apiBase}/invitation-layouts`),
          axios.get(`${this.apiBase}/invitation-components`),
        ]);
        this.categories = catRes.data.data || [];
        const layoutPayload = layoutRes.data.data || {};
        this.layouts = layoutPayload.items || [];
        this.viewOptions = layoutPayload.view_options || {};
        const componentPayload = componentRes.data.data || {};
        this.components = componentPayload.items || [];
        this.componentGroups = componentPayload.groups || {};
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal memuat katalog.', 'error');
      } finally {
        this.loading = false;
      }
    },

    openCreate() {
      if (this.tab === 'categories') {
        this.modalTitle = 'Tambah Kategori';
        this.catForm = { id: null, name: '', slug: '', icon: '', sort_order: 0, is_active: true };
      } else if (this.tab === 'layouts') {
        this.modalTitle = 'Tambah Model Layout';
        const firstView = Object.keys(this.viewOptions)[0] || 'invitations.layouts.classic';
        this.layoutForm = { id: null, key: '', label: '', description: '', view: firstView, sort_order: 0, is_active: true, is_builtin: false };
      } else {
        this.modalTitle = 'Tambah Komponen';
        this.componentForm = { id: null, key: '', label: '', component_type: 'section', section_type: 'opening', view: 'invitations.components.sections.opening', sort_order: 0, is_active: true };
      }
      this.modal = true;
    },

    editCategory(c) {
      this.modalTitle = 'Edit Kategori';
      this.catForm = { ...c };
      this.modal = true;
    },

    editLayout(l) {
      this.modalTitle = l.is_builtin ? 'Edit Layout Bawaan' : 'Edit Model Layout';
      this.layoutForm = { ...l };
      this.modal = true;
    },

    editComponent(c) {
      this.modalTitle = 'Edit Komponen';
      this.componentForm = { ...c };
      this.modal = true;
    },

    async save() {
      this.saving = true;
      try {
        if (this.tab === 'categories') {
          const body = { ...this.catForm };
          delete body.id;
          delete body.templates_count;
          if (this.catForm.id) {
            const { data } = await axios.put(`${this.apiBase}/invitation-categories/${this.catForm.id}`, body);
            this.patchCategory(data.data);
          } else {
            const { data } = await axios.post(`${this.apiBase}/invitation-categories`, body);
            this.categories.push(data.data);
          }
        } else if (this.tab === 'layouts') {
          const body = { ...this.layoutForm };
          delete body.id;
          if (this.layoutForm.is_builtin) {
            const { data } = await axios.put(`${this.apiBase}/invitation-layouts/builtin/${this.layoutForm.key}`, body);
            this.patchLayout(data.data);
          } else if (this.layoutForm.id) {
            const { data } = await axios.put(`${this.apiBase}/invitation-layouts/${this.layoutForm.id}`, body);
            this.patchLayout(data.data);
          } else {
            const { data } = await axios.post(`${this.apiBase}/invitation-layouts`, body);
            this.layouts.push(data.data);
          }
        } else {
          const body = { ...this.componentForm };
          delete body.id;
          delete body.is_system;
          if (this.componentForm.id) {
            const { data } = await axios.put(`${this.apiBase}/invitation-components/${this.componentForm.id}`, body);
            this.patchComponent(data.data);
          } else {
            const { data } = await axios.post(`${this.apiBase}/invitation-components`, body);
            this.components.push(data.data);
          }
        }
        this.modal = false;
        window.toast?.('Disimpan.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal menyimpan.', 'error');
      } finally {
        this.saving = false;
      }
    },

    async removeCategory(c) {
      const ok = await window.confirmDialog?.({ title: 'Hapus kategori?', message: c.name, confirmText: 'Hapus', variant: 'danger' });
      if (!ok) return;
      try {
        await axios.delete(`${this.apiBase}/invitation-categories/${c.id}`);
        this.categories = this.categories.filter((x) => x.id !== c.id);
        window.toast?.('Kategori dihapus.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal hapus.', 'error');
      }
    },

    async removeLayout(l) {
      if (l.is_builtin) return;
      const ok = await window.confirmDialog?.({ title: 'Hapus layout?', message: l.label, confirmText: 'Hapus', variant: 'danger' });
      if (!ok) return;
      try {
        await axios.delete(`${this.apiBase}/invitation-layouts/${l.id}`);
        this.layouts = this.layouts.filter((x) => x.id !== l.id);
        window.toast?.('Layout dihapus.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal hapus.', 'error');
      }
    },

    async removeComponent(c) {
      if (!c.id || c.is_system) return;
      const ok = await window.confirmDialog?.({ title: 'Hapus komponen?', message: c.label, confirmText: 'Hapus', variant: 'danger' });
      if (!ok) return;
      try {
        await axios.delete(`${this.apiBase}/invitation-components/${c.id}`);
        this.components = this.components.filter((x) => x.id !== c.id);
        window.toast?.('Komponen dihapus.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal hapus.', 'error');
      }
    },

    patchCategory(row) {
      const idx = this.categories.findIndex((x) => x.id === row.id);
      if (idx >= 0) this.categories[idx] = row;
    },

    patchLayout(row) {
      const idx = this.layouts.findIndex((x) => x.key === row.key);
      if (idx >= 0) this.layouts[idx] = row;
    },

    patchComponent(row) {
      const idx = this.components.findIndex((x) => x.key === row.key);
      if (idx >= 0) this.components[idx] = row;
    },
  };
}

Alpine.data('invitationCatalogManager', createInvitationCatalogManager);
